<div class="wrapper">
  <!-- Header -->
  <div class="heading text-type-2 mb-[20px] flex flex-row justify-between items-center">
    <div class="title text-type-2">{{ areaCalculationGroup.label || 'Area calculation' }} for {{ bidItemName }}</div>
    <mat-icon (click)="close()">close</mat-icon>
  </div>

  <!-- Summary Fields (shown when there are summary fields and data exists) -->
  @if (summaryFields.length && formData.data.length) {
    <div class="summary-section" [formGroup]="summaryFormGroup">
      @for (field of summaryFields; track field.id) {
        <div class="summary-field" [ngClass]="getSummaryFieldSpacing(field)">
          @switch (getSummaryFieldType(field)) {
            @case ('display_only') {
              <app-view-field
                [label]="field.label || field.name"
                [uom]="field.uom || ''"
                [value]="'' + (getSummaryFieldValue(field.id) ?? '')"
              />
            }
            @case ('boolean') {
              <div class="boolean-field flex justify-between items-center">
                <span class="boolean-label text-type-13">{{ field.label || field.name }}</span>
                <mat-slide-toggle
                  [checked]="summaryFormGroup.controls[field.id]?.value"
                  (change)="onSummaryFieldChange(field, $event.checked)"
                />
              </div>
            }
            @case ('number') {
              <app-number-input
                [isDecimalAllowed]="true"
                [decimalPlaces]="field.precision ?? 5"
                [maxLength]="24"
                [parentFormGroup]="summaryFormGroup"
                [controlName]="field.id"
                [label]="(field.label || field.name) + (field.uom ? ' (' + field.uom + ')' : '')"
                (checkForErrors)="onSummaryFieldChange(field, summaryFormGroup.controls[field.id]?.value)"
              />
            }
            @default {
              <app-text-input
                [parentFormGroup]="summaryFormGroup"
                [controlName]="field.id"
                [label]="field.label || field.name"
                (checkForErrors)="onSummaryFieldChange(field, summaryFormGroup.controls[field.id]?.value)"
              />
            }
          }
        </div>
      }
    </div>
  }

  <!-- SINGLE-LINE MODE (no sections) -->
  @if (!isMultiLine && !sections.length && formData.data.length) {
    <div class="single-line-fields" [formGroup]="dataFormGroups[formData.data[0]?.internalId]">
      <div class="fields-grid">
        @for (field of customFields; track field.id) {
          <div class="field" [ngClass]="[getFieldSpacing(field), isFullWidthField(field) ? 'full-width' : '']">
            <ng-container
              [ngTemplateOutlet]="fieldTemplate"
              [ngTemplateOutletContext]="{
                $implicit: field,
                dataItem: formData.data[0],
                formGroup: dataFormGroups[formData.data[0].internalId]
              }"
            />
          </div>
        }
      </div>
    </div>
  }

  <!-- SINGLE-LINE MODE (with sections, no multi-line) -->
  @if (!isMultiLine && sections.length && !hasSectionMultiLine && formData.data.length) {
    <div class="sections-mode">
      @for (section of getSortedSections(); track section.name) {
        <div class="section-accordion">
          <div
            (click)="toggleSection(formData.data[0].internalId, section.name)"
            class="section-header flex justify-between items-center cursor-pointer"
          >
            <div class="section-name text-type-8">{{ (section.label || section.name) | sentenceCase }}</div>
            <mat-icon
              [svgIcon]="isSectionExpanded(formData.data[0].internalId, section.name) ? 'chevron-up' : 'chevron-down'"
              class="chevron-icon"
            />
          </div>
          @if (isSectionExpanded(formData.data[0].internalId, section.name)) {
            <div class="section-content">
              <div class="fields-grid">
                @for (field of getSectionFields(section.name); track field.id) {
                  <div class="field" [ngClass]="[getFieldSpacing(field), isFullWidthField(field) ? 'full-width' : '']">
                    <ng-container
                      [ngTemplateOutlet]="fieldTemplate"
                      [ngTemplateOutletContext]="{
                        $implicit: field,
                        dataItem: formData.data[0],
                        formGroup: dataFormGroups[formData.data[0].internalId]
                      }"
                    />
                  </div>
                }
              </div>
            </div>
          }
        </div>
      }
    </div>
  }

  <!-- MIXED SECTIONS MODE (single-line sections + multi-line sections) -->
  @if (!isMultiLine && hasSectionMultiLine && formData.data.length) {
    <div class="mixed-sections-mode">
      <!-- Single-line sections (e.g., Information) -->
      @for (section of getSingleLineSections(); track section.name) {
        <div class="section-accordion">
          <div
            (click)="toggleSection(formData.data[0].internalId, section.name)"
            class="section-header flex justify-between items-center cursor-pointer"
          >
            <div class="section-name text-type-8">{{ (section.label || section.name) | sentenceCase }}</div>
            <mat-icon
              [svgIcon]="isSectionExpanded(formData.data[0].internalId, section.name) ? 'chevron-up' : 'chevron-down'"
              class="chevron-icon"
            />
          </div>
          @if (isSectionExpanded(formData.data[0].internalId, section.name)) {
            <div class="section-content">
              <div class="fields-grid">
                @for (field of getSectionFields(section.name); track field.id) {
                  <div class="field" [ngClass]="[getFieldSpacing(field), isFullWidthField(field) ? 'full-width' : '']">
                    <ng-container
                      [ngTemplateOutlet]="fieldTemplate"
                      [ngTemplateOutletContext]="{
                        $implicit: field,
                        dataItem: formData.data[0],
                        formGroup: dataFormGroups[formData.data[0].internalId]
                      }"
                    />
                  </div>
                }
              </div>
            </div>
          }
        </div>
      }

      <!-- Multi-line sections (e.g., Density) -->
      @for (section of getMultiLineSections(); track section.name) {
        <div class="multi-line-section-wrapper">
          <div class="section-header-with-add flex justify-between items-center mb-[12px] mt-[16px]">
            <div class="section-name text-type-8">{{ (section.label || section.name) | sentenceCase }}</div>
            <div (click)="addMultiLineSectionRow(section)" class="add-row-btn flex items-center cursor-pointer">
              <mat-icon class="add-icon mr-[8px]" svgIcon="plus" />
              Add another
            </div>
          </div>

          @for (dataItem of getMultiLineSectionDataItems(); track dataItem.internalId) {
            <div
              [class.is-expanded]="expandedDataItemId === dataItem.internalId"
              class="data-item-accordion"
            >
              <div class="data-item-header flex justify-between items-start">
                <div
                  (click)="toggleDataItem(dataItem.internalId)"
                  class="left flex flex-col grow cursor-pointer"
                >
                  <div class="data-item-title text-type-8">{{ getMultiLineSectionRowTitle(dataItem, section) | sentenceCase }}</div>
                </div>
                <div class="right flex items-start gap-[12px]">
                  <mat-icon
                    (click)="toggleDataItem(dataItem.internalId)"
                    [svgIcon]="expandedDataItemId === dataItem.internalId ? 'chevron-up' : 'chevron-down'"
                    class="chevron-icon"
                  />
                  @if (canDeleteMultiLineSectionRow()) {
                    <mat-icon
                      [matMenuTriggerFor]="sectionRowMenu"
                      class="menu-icon"
                    >more_horiz</mat-icon>
                    <mat-menu #sectionRowMenu="matMenu">
                      <button (click)="deleteMultiLineSectionRow(dataItem)" mat-menu-item>
                        <mat-icon svgIcon="trash" />
                        Delete
                      </button>
                    </mat-menu>
                  }
                </div>
              </div>

              @if (expandedDataItemId === dataItem.internalId && dataFormGroups[dataItem.internalId]) {
                <div class="data-item-content">
                  <div class="fields-grid">
                    @for (field of getSectionFields(section.name); track field.id) {
                      <div class="field" [ngClass]="[getFieldSpacing(field), isFullWidthField(field) ? 'full-width' : '']">
                        <ng-container
                          [ngTemplateOutlet]="fieldTemplate"
                          [ngTemplateOutletContext]="{
                            $implicit: field,
                            dataItem: dataItem,
                            formGroup: dataFormGroups[dataItem.internalId]
                          }"
                        />
                      </div>
                    }
                  </div>
                </div>
              }
            </div>
          }
        </div>
      }
    </div>
  }

  <!-- MULTI-LINE MODE (accordion per data item) -->
  @if (isMultiLine) {
    <div class="multi-line-section">
      <!-- Add another button (top right) -->
      <div class="flex justify-end mb-[20px]">
        <div (click)="addDataItem()" class="add-row-btn flex items-center cursor-pointer">
          <mat-icon class="add-icon mr-[8px]" svgIcon="plus" />
          Add another
        </div>
      </div>

      @for (dataItem of formData.data; track dataItem.internalId) {
        <div
          [class.is-expanded]="expandedDataItemId === dataItem.internalId"
          class="data-item-accordion"
        >
          <!-- Data Item Header -->
          <div class="data-item-header flex justify-between items-start">
            <div
              (click)="toggleDataItem(dataItem.internalId)"
              class="left flex flex-col grow cursor-pointer"
            >
              <div class="data-item-title text-type-8">{{ (getDataItemTitle(dataItem) || 'New') | sentenceCase }}</div>
              @if (getDataItemDescription(dataItem)) {
                <div class="data-item-description text-type-15">{{ getDataItemDescription(dataItem) }}</div>
              }
            </div>
            <div class="right flex items-start gap-[12px]">
              <mat-icon
                (click)="toggleDataItem(dataItem.internalId)"
                [svgIcon]="expandedDataItemId === dataItem.internalId ? 'chevron-up' : 'chevron-down'"
                class="chevron-icon"
              />
              <mat-icon
                [matMenuTriggerFor]="dataItemMenu"
                class="menu-icon"
              >more_horiz</mat-icon>
              <mat-menu #dataItemMenu="matMenu">
                <button (click)="deleteDataItem(dataItem)" mat-menu-item>
                  <mat-icon svgIcon="trash" />
                  Delete
                </button>
              </mat-menu>
            </div>
          </div>

          <!-- Data Item Content -->
          @if (expandedDataItemId === dataItem.internalId && dataFormGroups[dataItem.internalId]) {
            <div class="data-item-content">
              <!-- Without sections -->
              @if (!sections.length) {
                <div class="fields-grid">
                  @for (field of getVisibleFieldsForDataItem(dataItem); track field.id) {
                    <div class="field" [ngClass]="[getFieldSpacing(field), isFullWidthField(field) ? 'full-width' : '']">
                      <ng-container
                        [ngTemplateOutlet]="fieldTemplate"
                        [ngTemplateOutletContext]="{
                          $implicit: field,
                          dataItem: dataItem,
                          formGroup: dataFormGroups[dataItem.internalId]
                        }"
                      />
                    </div>
                  }
                </div>
              }

              <!-- With sections -->
              @if (sections.length) {
                @for (section of getSortedSections(); track section.name) {
                  <div class="section-accordion">
                    <div
                      (click)="toggleSection(dataItem.internalId, section.name)"
                      class="section-header flex justify-between items-center cursor-pointer"
                    >
                      <div class="section-name text-type-8">{{ (section.label || section.name) | sentenceCase }}</div>
                      <mat-icon
                        [svgIcon]="isSectionExpanded(dataItem.internalId, section.name) ? 'chevron-up' : 'chevron-down'"
                        class="chevron-icon"
                      />
                    </div>
                    @if (isSectionExpanded(dataItem.internalId, section.name)) {
                      <div class="section-content">
                        <div class="fields-grid">
                          @for (field of getSectionFields(section.name); track field.id) {
                            <div class="field" [ngClass]="[getFieldSpacing(field), isFullWidthField(field) ? 'full-width' : '']">
                              <ng-container
                                [ngTemplateOutlet]="fieldTemplate"
                                [ngTemplateOutletContext]="{
                                  $implicit: field,
                                  dataItem: dataItem,
                                  formGroup: dataFormGroups[dataItem.internalId]
                                }"
                              />
                            </div>
                          }
                        </div>
                      </div>
                    }
                  </div>
                }
              }
            </div>
          }
        </div>
      }

    </div>
  }

  <!-- Delete Area Calculation -->
  @if (hasExistingData()) {
    <button (click)="deleteAreaCalculation()" class="delete-btn mt-[40px] flex items-center gap-[8px]">
      <mat-icon svgIcon="trash" class="delete-icon" />
      Delete {{ areaCalculationGroup.label?.toLowerCase() || 'area calculation' }}
    </button>
  }

  <!-- Done Button -->
  <div class="action-section mt-[40px]">
    <app-primary-button
      (handleClick)="done()"
      class="w-full"
    >
      Done
    </app-primary-button>
  </div>
</div>

<!-- Reusable Field Template -->
<ng-template #fieldTemplate let-field let-dataItem="dataItem" let-formGroup="formGroup">
  @switch (getFieldType(field)) {
    @case ('display_only') {
      <app-view-field
        [label]="field.label || field.name"
        [uom]="field.uom || ''"
        [value]="'' + (getFieldValue(dataItem, field.id) ?? '')"
      />
    }
    @case ('dropdown') {
      <app-dropdown
        [options]="getPickListOptions(field)"
        [parentFormGroup]="formGroup"
        [controlName]="field.id"
        [label]="field.label || field.name"
        [sentenceCase]="true"
        (checkForErrors)="onFieldChange(dataItem, field, formGroup.controls[field.id]?.value)"
      />
    }
    @case ('number') {
      <app-number-input
        [isDecimalAllowed]="true"
        [decimalPlaces]="field.ui_config?.precision ?? field.precision ?? 5"
        [maxLength]="24"
        [parentFormGroup]="formGroup"
        [controlName]="field.id"
        [label]="(field.label || field.name) + (field.uom ? ' (' + field.uom + ')' : '')"
        [suffixText]="field.uom || ''"
        (checkForErrors)="onFieldChange(dataItem, field, formGroup.controls[field.id]?.value)"
      />
    }
    @case ('date') {
      <app-date-picker
        [parentFormGroup]="formGroup"
        [controlName]="field.id"
        [label]="field.label || field.name"
        (checkForErrors)="onFieldChange(dataItem, field, formGroup.controls[field.id]?.value)"
      />
    }
    @case ('time') {
      <app-time-select
        [parentFormGroup]="formGroup"
        [controlName]="field.id"
        [timePeriodControlName]="'timePeriod_' + field.id"
        [amPmControlName]="'amPm_' + field.id"
        [label]="field.label || field.name"
        (checkForErrors)="onFieldChange(dataItem, field, formGroup.controls[field.id]?.value)"
      />
    }
    @case ('boolean') {
      <div class="boolean-field flex justify-between items-center">
        <span class="boolean-label text-type-13">{{ field.label || field.name }}</span>
        <mat-slide-toggle
          [checked]="formGroup.controls[field.id]?.value"
          (change)="onFieldChange(dataItem, field, $event.checked)"
        />
      </div>
    }
    @default {
      <app-text-input
        [parentFormGroup]="formGroup"
        [controlName]="field.id"
        [label]="field.label || field.name"
        (checkForErrors)="onFieldChange(dataItem, field, formGroup.controls[field.id]?.value)"
      />
    }
  }
</ng-template>
